export type TypographyFontRole = 'title' | 'body' | 'button' | 'mono';

export interface TypographyFontOption {
  id: string;
  label: string;
  stack: string;
  note: string;
  roles: TypographyFontRole[];
}

export interface TypographyPreset {
  id: string;
  label: string;
  description: string;
  titleFontId: string;
  titleLetterSpacing: number;
  bodyFontId: string;
  buttonFontId: string;
  monoFontId: string;
  bodyLetterSpacing: number;
  bodyFontWeight: number;
  bodyFontSize: number;
}

export interface TypographySettings {
  presetId: string;
  titleFontId: string;
  titleLetterSpacing: number;
  bodyFontId: string;
  buttonFontId: string;
  monoFontId: string;
  bodyLetterSpacing: number;
  bodyFontWeight: number;
  bodyFontSize: number;
}

export const TYPOGRAPHY_STORAGE_KEY = 'lifedrawing-typography-settings-v4';
export const FIXED_TITLE_FONT_ID = 'fraunces';
export const FIXED_MONO_FONT_ID = 'jetbrains-mono';
export const DEFAULT_COPY_FONT_ID = 'literata';
export const DEFAULT_TITLE_LETTER_SPACING = -0.03;
export const MIN_TITLE_LETTER_SPACING = -0.06;
export const MAX_TITLE_LETTER_SPACING = 0.04;
export const DEFAULT_BODY_LETTER_SPACING = 0;
export const MIN_BODY_LETTER_SPACING = -0.02;
export const MAX_BODY_LETTER_SPACING = 0.08;
export const DEFAULT_BODY_FONT_WEIGHT = 400;
export const MIN_BODY_FONT_WEIGHT = 300;
export const MAX_BODY_FONT_WEIGHT = 700;
export const DEFAULT_BODY_FONT_SIZE = 16;
export const MIN_BODY_FONT_SIZE = 14;
export const MAX_BODY_FONT_SIZE = 20;

export const FONT_OPTIONS: TypographyFontOption[] = [
  {
    id: 'fraunces',
    label: 'Fraunces',
    stack: '"Fraunces", Georgia, ui-serif, serif',
    note: 'Fixed title face. Titles always use weight 300.',
    roles: ['title'],
  },
  {
    id: 'literata',
    label: 'Literata',
    stack: '"Literata", Georgia, ui-serif, serif',
    note: 'Primary copy face for body text, forms, labels, and buttons.',
    roles: ['body', 'button'],
  },
  {
    id: 'figtree',
    label: 'Figtree',
    stack: '"Figtree", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    note: 'Elegant, balanced, and close to the current product feel.',
    roles: ['body', 'button'],
  },
  {
    id: 'inter',
    label: 'Inter',
    stack: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    note: 'Neutral workhorse sans for calm, readable UI.',
    roles: ['body', 'button'],
  },
  {
    id: 'dm-sans',
    label: 'DM Sans',
    stack: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    note: 'Friendly geometric sans with a softer product feel.',
    roles: ['body', 'button'],
  },
  {
    id: 'instrument-sans',
    label: 'Instrument Sans',
    stack: '"Instrument Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    note: 'Crisp and editorial without getting decorative.',
    roles: ['body', 'button'],
  },
  {
    id: 'ibm-plex-sans',
    label: 'IBM Plex Sans',
    stack: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    note: 'Structured, precise sans for admin and dashboard-heavy pages.',
    roles: ['body', 'button'],
  },
  {
    id: 'plus-jakarta-sans',
    label: 'Plus Jakarta Sans',
    stack: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    note: 'Refined geometric sans with a slightly premium tone.',
    roles: ['body', 'button'],
  },
  {
    id: 'source-sans-3',
    label: 'Source Sans 3',
    stack: '"Source Sans 3", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    note: 'Quiet, readable, and especially good for forms.',
    roles: ['body', 'button'],
  },
  {
    id: 'work-sans',
    label: 'Work Sans',
    stack: '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    note: 'Clean, practical, and slightly warmer than a default system face.',
    roles: ['body', 'button'],
  },
  {
    id: 'jetbrains-mono',
    label: 'JetBrains Mono',
    stack: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    note: 'Current utility mono for labels, counters, and technical metadata.',
    roles: ['mono'],
  },
];

export const TYPOGRAPHY_PRESETS: TypographyPreset[] = [
  {
    id: 'lifedrawing-clean',
    label: 'LifeDrawing Literata',
    description: 'Fraunces titles with balanced Literata copy.',
    titleFontId: FIXED_TITLE_FONT_ID,
    titleLetterSpacing: DEFAULT_TITLE_LETTER_SPACING,
    bodyFontId: DEFAULT_COPY_FONT_ID,
    buttonFontId: DEFAULT_COPY_FONT_ID,
    monoFontId: FIXED_MONO_FONT_ID,
    bodyLetterSpacing: DEFAULT_BODY_LETTER_SPACING,
    bodyFontWeight: DEFAULT_BODY_FONT_WEIGHT,
    bodyFontSize: DEFAULT_BODY_FONT_SIZE,
  },
  {
    id: 'studio-literata',
    label: 'Studio Literata',
    description: 'A slightly larger setting for product and dashboard pages.',
    titleFontId: FIXED_TITLE_FONT_ID,
    titleLetterSpacing: DEFAULT_TITLE_LETTER_SPACING,
    bodyFontId: DEFAULT_COPY_FONT_ID,
    buttonFontId: DEFAULT_COPY_FONT_ID,
    monoFontId: FIXED_MONO_FONT_ID,
    bodyLetterSpacing: 0.005,
    bodyFontWeight: 400,
    bodyFontSize: 17,
  },
  {
    id: 'compact-literata',
    label: 'Compact Literata',
    description: 'A tighter copy setting for dense admin surfaces.',
    titleFontId: FIXED_TITLE_FONT_ID,
    titleLetterSpacing: DEFAULT_TITLE_LETTER_SPACING,
    bodyFontId: DEFAULT_COPY_FONT_ID,
    buttonFontId: DEFAULT_COPY_FONT_ID,
    monoFontId: FIXED_MONO_FONT_ID,
    bodyLetterSpacing: 0,
    bodyFontWeight: 400,
    bodyFontSize: 15,
  },
  {
    id: 'strong-literata',
    label: 'Strong Literata',
    description: 'A sturdier copy weight for stronger interface contrast.',
    titleFontId: FIXED_TITLE_FONT_ID,
    titleLetterSpacing: DEFAULT_TITLE_LETTER_SPACING,
    bodyFontId: DEFAULT_COPY_FONT_ID,
    buttonFontId: DEFAULT_COPY_FONT_ID,
    monoFontId: FIXED_MONO_FONT_ID,
    bodyLetterSpacing: 0.002,
    bodyFontWeight: 500,
    bodyFontSize: 16,
  },
  {
    id: 'open-literata',
    label: 'Open Literata',
    description: 'A roomier copy setting for public-facing pages.',
    titleFontId: FIXED_TITLE_FONT_ID,
    titleLetterSpacing: DEFAULT_TITLE_LETTER_SPACING,
    bodyFontId: DEFAULT_COPY_FONT_ID,
    buttonFontId: DEFAULT_COPY_FONT_ID,
    monoFontId: FIXED_MONO_FONT_ID,
    bodyLetterSpacing: 0.015,
    bodyFontWeight: 400,
    bodyFontSize: 18,
  },
];

export const DEFAULT_TYPOGRAPHY_SETTINGS: TypographySettings = {
  presetId: TYPOGRAPHY_PRESETS[0].id,
  titleFontId: TYPOGRAPHY_PRESETS[0].titleFontId,
  titleLetterSpacing: TYPOGRAPHY_PRESETS[0].titleLetterSpacing,
  bodyFontId: TYPOGRAPHY_PRESETS[0].bodyFontId,
  buttonFontId: TYPOGRAPHY_PRESETS[0].buttonFontId,
  monoFontId: TYPOGRAPHY_PRESETS[0].monoFontId,
  bodyLetterSpacing: TYPOGRAPHY_PRESETS[0].bodyLetterSpacing,
  bodyFontWeight: TYPOGRAPHY_PRESETS[0].bodyFontWeight,
  bodyFontSize: TYPOGRAPHY_PRESETS[0].bodyFontSize,
};

export function findFont(fontId: string) {
  return FONT_OPTIONS.find((font) => font.id === fontId) || FONT_OPTIONS[0];
}

export function getFontsForRole(role: TypographyFontRole) {
  return FONT_OPTIONS.filter((font) => font.roles.includes(role));
}

export function findFontForRole(fontId: string, role: TypographyFontRole) {
  const font = FONT_OPTIONS.find((item) => item.id === fontId && item.roles.includes(role));
  return font || getFontsForRole(role)[0];
}

export function getPresetSettings(presetId: string): TypographySettings {
  const preset = TYPOGRAPHY_PRESETS.find((item) => item.id === presetId) || TYPOGRAPHY_PRESETS[0];

  return {
    presetId: preset.id,
    titleFontId: preset.titleFontId,
    titleLetterSpacing: preset.titleLetterSpacing,
    bodyFontId: preset.bodyFontId,
    buttonFontId: preset.buttonFontId,
    monoFontId: preset.monoFontId,
    bodyLetterSpacing: preset.bodyLetterSpacing,
    bodyFontWeight: preset.bodyFontWeight,
    bodyFontSize: preset.bodyFontSize,
  };
}

function clampTitleLetterSpacing(value: unknown) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_TITLE_LETTER_SPACING;
  }

  return Math.min(MAX_TITLE_LETTER_SPACING, Math.max(MIN_TITLE_LETTER_SPACING, value));
}

function clampBodyLetterSpacing(value: unknown) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_BODY_LETTER_SPACING;
  }

  return Math.min(MAX_BODY_LETTER_SPACING, Math.max(MIN_BODY_LETTER_SPACING, value));
}

function clampBodyFontWeight(value: unknown) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_BODY_FONT_WEIGHT;
  }

  return Math.min(MAX_BODY_FONT_WEIGHT, Math.max(MIN_BODY_FONT_WEIGHT, Math.round(value / 50) * 50));
}

function clampBodyFontSize(value: unknown) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_BODY_FONT_SIZE;
  }

  return Math.min(MAX_BODY_FONT_SIZE, Math.max(MIN_BODY_FONT_SIZE, value));
}

export function normalizeTypographySettings(settings: Partial<TypographySettings>): TypographySettings {
  const merged = {
    ...DEFAULT_TYPOGRAPHY_SETTINGS,
    ...settings,
  };
  const presetId = merged.presetId === 'custom' || TYPOGRAPHY_PRESETS.some((preset) => preset.id === merged.presetId)
    ? merged.presetId
    : DEFAULT_TYPOGRAPHY_SETTINGS.presetId;

  return {
    presetId,
    titleFontId: FIXED_TITLE_FONT_ID,
    titleLetterSpacing: clampTitleLetterSpacing(merged.titleLetterSpacing),
    bodyFontId: findFontForRole(merged.bodyFontId, 'body').id,
    buttonFontId: findFontForRole(merged.buttonFontId, 'button').id,
    monoFontId: FIXED_MONO_FONT_ID,
    bodyLetterSpacing: clampBodyLetterSpacing(merged.bodyLetterSpacing),
    bodyFontWeight: clampBodyFontWeight(merged.bodyFontWeight),
    bodyFontSize: clampBodyFontSize(merged.bodyFontSize),
  };
}
